import { Fragment, useMemo, useState } from 'react';
import { Plus, FileText, Lock, ChevronDown, ChevronRight, EyeOff, MoreVertical, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { groupAssignments, type AssignmentBucket } from './assignmentGroups';
import { useDeleteAssignment, useSetAssignmentPosted } from './useAssignmentMutations';
import AssignmentDialog from './AssignmentDialog';
import { AssignmentScoreEditor } from './GradeEntryDialog';
import { eligibleStudentsForAssignment } from './assignmentEligibility';
import type { ClassOfferingDetail, GradebookFromQuery } from './classDetailTypes';

type Assignment = NonNullable<ClassOfferingDetail['assignments']>[number];

interface Props {
  classOfferingId: string;
  gradebooks: ReadonlyArray<GradebookFromQuery>;
  assignments: ReadonlyArray<Assignment>;
  onChanged: () => void;
}

export default function AssignmentsSection({
  classOfferingId,
  gradebooks,
  assignments,
  onChanged,
}: Props) {
  const [editing, setEditing] = useState<Assignment | null>(null);
  const [creating, setCreating] = useState(false);
  const [expandedAssignmentId, setExpandedAssignmentId] = useState<string | null>(null);
  const [deleteAssignment] = useDeleteAssignment();
  const [setAssignmentPosted] = useSetAssignmentPosted();
  const gradebookById = useMemo(
    () => new Map(gradebooks.map((gradebook) => [gradebook.id, gradebook])),
    [gradebooks]
  );
  const openPointsGradebooks = gradebooks.filter(
    (gradebook) => gradebook.gradingMode === 'POINTS' && !gradebook.finalized
  );

  /**
   * Collapsed state per group, once the teacher has actually touched one. Until
   * then each group follows its own default, so a class whose backlog clears
   * mid-session does not keep an empty section pinned open.
   */
  const [openOverrides, setOpenOverrides] = useState<Partial<Record<AssignmentBucket, boolean>>>({});
  const [search, setSearch] = useState('');

  const handleDelete = async (a: Assignment) => {
    if (!window.confirm(`Delete assignment "${a.name}"? Any scores on it will be removed.`)) return;
    await deleteAssignment({ variables: { input: { id: a.id } } });
    onChanged();
  };

  const handleTogglePosted = async (a: Assignment) => {
    if (!a.posted && !window.confirm(
      `Post "${a.name}"? Entered scores will start counting toward grades. Blank scores remain ungraded; scores marked Missing count as zero.`
    )) return;
    await setAssignmentPosted({ variables: { input: { id: a.id, posted: !a.posted } } });
    onChanged();
  };

  const eligibleStudents = (assignment: Assignment) =>
    eligibleStudentsForAssignment(assignment, gradebooks);

  /**
   * What the teacher has decided for this assignment. A number, Missing and
   * Excused are all decisions; only a blank is work left to do. Missing in
   * particular counts as zero toward the grade, so an assignment where everyone
   * is scored or marked is finished, not part of the grading backlog.
   */
  const scoreBreakdown = (assignment: Assignment) => {
    const eligibleIds = new Set(eligibleStudents(assignment).map((student) => student.id));
    const mine = (assignment.scores ?? []).filter(
      (score) => score.person && eligibleIds.has(score.person.id)
    );
    const countOf = (state: string) => mine.filter((score) => score.state === state).length;
    const graded = countOf('GRADED');
    const missing = countOf('MISSING');
    const excused = countOf('EXCUSED');
    return { graded, missing, excused, resolved: graded + missing + excused };
  };

  const assignmentIsFinalized = (assignment: Assignment) =>
    (assignment.targets ?? []).some(
      (target) => gradebookById.get(target.gradebook.id)?.finalized
    );

  const query = search.trim().toLowerCase();
  const matches = (assignment: Assignment) =>
    !query
    || assignment.name.toLowerCase().includes(query)
    || (assignment.targets ?? []).some((target) =>
      target.gradebook.name.toLowerCase().includes(query)
      || (target.gradebookCategory?.name ?? '').toLowerCase().includes(query));

  const visible = useMemo(
    () => assignments.filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignments, query]
  );

  const groups = useMemo(
    () => groupAssignments(visible, (assignment) => ({
      resolvedCount: scoreBreakdown(assignment).resolved,
      eligibleCount: eligibleStudents(assignment).length,
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, gradebooks]
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Assignments ({assignments.length})
            </CardTitle>
            <CardDescription>
              Expand an assignment to enter or review scores for the class.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                // Not type="search": browsers add their own clear button, which
                // would sit on top of the one below.
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search assignments…"
                aria-label="Search assignments"
                className="h-9 w-56 pl-8 pr-8"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear assignment search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => setCreating(true)}
              disabled={openPointsGradebooks.length === 0}
              title={openPointsGradebooks.length === 0 ? 'Reopen a points gradebook before adding assignments' : undefined}
            >
              <Plus className="h-4 w-4 mr-1" /> Add Assignment
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {assignments.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground mb-3">
              No assignments yet. Add one to start tracking grades.
            </p>
            <Button onClick={() => setCreating(true)} disabled={openPointsGradebooks.length === 0}>
              <Plus className="h-4 w-4 mr-1" /> Add Assignment
            </Button>
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground">
              No assignments match “{search.trim()}”.
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setSearch('')}>
              Clear search
            </Button>
          </div>
        ) : (
          groups.map((group) => {
            const open = query ? true : (openOverrides[group.key] ?? group.defaultOpen);
            return (
              <Collapsible
                key={group.key}
                open={open}
                onOpenChange={(next) => setOpenOverrides((current) => ({ ...current, [group.key]: next }))}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-sm py-1 text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${group.title}`}
                  >
                    {open
                      ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span className="font-semibold">{group.title}</span>
                    <Badge variant={group.key === 'needsGrading' ? 'default' : 'secondary'}>
                      {group.assignments.length}
                    </Badge>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-3 font-medium">Name</th>
                          <th className="text-left p-3 font-medium">Gradebooks</th>
                          <th className="text-left p-3 font-medium">Due</th>
                          <th className="text-right p-3 font-medium">Points</th>
                          <th className="text-right p-3 font-medium">Graded</th>
                          <th className="text-right p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                {group.assignments.map((a) => {
                  const finalized = assignmentIsFinalized(a);
                  const expanded = expandedAssignmentId === a.id;
                  return (
                    <Fragment key={a.id}>
                      <tr className="border-b last:border-0">
                        <td className="p-3 font-medium">
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 rounded-sm text-left hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={finalized ? `${a.name} is locked` : `Enter grades for ${a.name}`}
                            aria-expanded={expanded}
                            aria-controls={`assignment-score-panel-${a.id}`}
                            disabled={finalized}
                            onClick={() => setExpandedAssignmentId(expanded ? null : a.id)}
                          >
                            {expanded
                              ? <ChevronDown className="h-4 w-4 shrink-0" />
                              : <ChevronRight className="h-4 w-4 shrink-0" />}
                            <span>{a.name}</span>
                            {!a.posted && (
                              <Badge variant="secondary" className="gap-1">
                                <EyeOff className="h-3 w-3" />Not posted
                              </Badge>
                            )}
                            {finalized && <Badge className="gap-1"><Lock className="h-3 w-3" />Finalized</Badge>}
                          </button>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {(a.targets ?? []).map((target) => (
                              <Badge key={target.id} variant="outline">
                                {target.gradebook.name} · {target.gradebookCategory?.name
                                  ?? (gradebookById.get(target.gradebook.id)?.categories.length === 0
                                    ? 'Total points'
                                    : 'Uncategorized')}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-3 text-muted-foreground">{String(a.dueOn ?? '')}</td>
                        <td className="p-3 text-right">
                          {Number(a.pointsPossible) === 0
                            ? <Badge variant="secondary">Extra credit</Badge>
                            : a.pointsPossible}
                        </td>
                        <td className="p-3 text-right text-muted-foreground">
                          {(() => {
                            const breakdown = scoreBreakdown(a);
                            const notes = [
                              breakdown.missing > 0 ? `${breakdown.missing} missing` : null,
                              breakdown.excused > 0 ? `${breakdown.excused} excused` : null,
                            ].filter(Boolean).join(' · ');
                            return (
                              <>
                                {breakdown.resolved} / {eligibleStudents(a).length}
                                {notes && <span className="block text-xs">{notes}</span>}
                              </>
                            );
                          })()}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                disabled={finalized}
                                aria-label={`Actions for ${a.name}`}
                                title={finalized ? 'Reopen the targeted gradebook first' : 'Actions'}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                disabled={finalized}
                                onSelect={() => handleTogglePosted(a)}
                              >
                                {a.posted ? 'Unpost' : 'Post'}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={finalized}
                                onSelect={() => setEditing(a)}
                              >
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={finalized}
                                className="text-destructive focus:text-destructive"
                                onSelect={() => handleDelete(a)}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                      {expanded && !finalized && (
                        <tr className="border-b bg-muted/10">
                          <td colSpan={6} className="p-0">
                            <div id={`assignment-score-panel-${a.id}`} className="space-y-3 p-4">
                              <div>
                                <h3 className="font-semibold">Scores — {a.name}</h3>
                                <p className="text-xs text-muted-foreground">
                                  Enter points (including 0), choose Missing or Excused, or leave blank for ungraded. Missing counts as zero.
                                </p>
                              </div>
                              <AssignmentScoreEditor
                                assignment={a}
                                students={eligibleStudents(a)}
                                onComplete={onChanged}
                                resetKey={expandedAssignmentId}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                      </tbody>
                    </table>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })
        )}
      </CardContent>

      {(creating || editing) && (
        <AssignmentDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setCreating(false);
              setEditing(null);
            }
          }}
          classOfferingId={classOfferingId}
          gradebooks={gradebooks}
          assignment={editing}
          onComplete={() => {
            setCreating(false);
            setEditing(null);
            onChanged();
          }}
        />
      )}

    </Card>
  );
}
